// Which region's festival dates the device uses (D266). Lunar festivals are
// counted from local sunrise and Eid from local moon sighting, so a family
// in Toronto can keep Holi a day before New Delhi. The device's own time
// zone picks the table; nothing is sent to the server and no location is
// asked for. Always automatic: there is no setting.

export type FestivalRegion =
  | 'india'
  | 'canada-east'
  | 'canada-west'
  | 'uk'
  | 'gulf'
  | 'singapore'
  | 'australia';

export const FESTIVAL_REGIONS: readonly FestivalRegion[] = [
  'india',
  'canada-east',
  'canada-west',
  'uk',
  'gulf',
  'singapore',
  'australia',
];

/** Common zones, named explicitly so daylight saving can't move them. */
const ZONE_REGIONS: Record<string, FestivalRegion> = {
  'Asia/Kolkata': 'india',
  'Asia/Calcutta': 'india',

  // Eastern and Central North America: closer to Toronto's sunrise.
  'America/Toronto': 'canada-east',
  'America/Montreal': 'canada-east',
  'America/Nipigon': 'canada-east',
  'America/Thunder_Bay': 'canada-east',
  'America/Iqaluit': 'canada-east',
  'America/New_York': 'canada-east',
  'America/Detroit': 'canada-east',
  'America/Indiana/Indianapolis': 'canada-east',
  'America/Kentucky/Louisville': 'canada-east',
  'America/Halifax': 'canada-east',
  'America/Moncton': 'canada-east',
  'America/Glace_Bay': 'canada-east',
  'America/St_Johns': 'canada-east',
  'America/Chicago': 'canada-east',
  'America/Winnipeg': 'canada-east',
  'America/Regina': 'canada-east',
  'US/Eastern': 'canada-east',
  'US/Central': 'canada-east',
  'Canada/Eastern': 'canada-east',
  'Canada/Atlantic': 'canada-east',
  'Canada/Newfoundland': 'canada-east',
  'Canada/Central': 'canada-east',

  // Mountain and Pacific: Edmonton and Calgary (America/Edmonton) are an
  // hour from Vancouver and two from Toronto, so they go west.
  'America/Vancouver': 'canada-west',
  'America/Edmonton': 'canada-west',
  'America/Whitehorse': 'canada-west',
  'America/Dawson_Creek': 'canada-west',
  'America/Yellowknife': 'canada-west',
  'America/Los_Angeles': 'canada-west',
  'America/Denver': 'canada-west',
  'America/Phoenix': 'canada-west',
  'America/Boise': 'canada-west',
  'America/Anchorage': 'canada-west',
  'Pacific/Honolulu': 'canada-west',
  'US/Pacific': 'canada-west',
  'US/Mountain': 'canada-west',
  'Canada/Pacific': 'canada-west',
  'Canada/Mountain': 'canada-west',

  'Europe/London': 'uk',
  'Europe/Dublin': 'uk',
  'Europe/Belfast': 'uk',
  GB: 'uk',

  'Asia/Dubai': 'gulf',
  'Asia/Muscat': 'gulf',
  'Asia/Qatar': 'gulf',
  'Asia/Riyadh': 'gulf',
  'Asia/Kuwait': 'gulf',
  'Asia/Bahrain': 'gulf',

  'Asia/Singapore': 'singapore',
  'Asia/Kuala_Lumpur': 'singapore',
  Singapore: 'singapore',
};

/** Each region's reference city's standard UTC offset, in minutes. */
const REGION_OFFSETS: Record<FestivalRegion, number> = {
  india: 330, // New Delhi
  'canada-east': -300, // Toronto
  'canada-west': -480, // Vancouver
  uk: 0, // London
  gulf: 240, // Dubai
  singapore: 480,
  australia: 600, // Sydney
};

/**
 * The region whose reference offset is closest. Not round the clock: UTC+13
 * and UTC−11 share a clock but not a calendar day, and festival days are
 * calendar days.
 */
export function nearestRegionByOffset(offsetMinutes: number): FestivalRegion {
  let best: FestivalRegion = 'india';
  let bestDistance = Infinity;
  for (const region of FESTIVAL_REGIONS) {
    const distance = Math.abs(offsetMinutes - REGION_OFFSETS[region]);
    // Strictly closer, so a tie keeps the earlier region in the list (India first).
    if (distance < bestDistance) {
      best = region;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The region for an IANA time zone: the explicit map, any Australia/* zone,
 * else the nearest region by the zone's current UTC offset (minutes east of
 * UTC). India when there's nothing to go on.
 */
export function regionForTimeZone(
  zone: string | null | undefined,
  offsetMinutes: number | null | undefined
): FestivalRegion {
  if (zone) {
    const known = ZONE_REGIONS[zone];
    if (known) return known;
    if (zone.startsWith('Australia/')) return 'australia';
  }
  if (typeof offsetMinutes === 'number' && Number.isFinite(offsetMinutes)) {
    return nearestRegionByOffset(offsetMinutes);
  }
  return 'india';
}

/** This device's region, from its time zone (D266). India if that fails. */
export function festivalRegion(now = new Date()): FestivalRegion {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // getTimezoneOffset is minutes *behind* UTC; flip it to minutes east.
    return regionForTimeZone(zone, -now.getTimezoneOffset());
  } catch {
    return 'india';
  }
}
